import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Workflow, ListChecks, Upload, LineChart, Route, ShieldCheck, Lock, Gauge, Megaphone, Wallet, FileText } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { usePipelineTrackerAccess } from '../../hooks/pipelineTracker/usePipelineTrackerAccess';
import { useSnapshotFreshness } from '../../hooks/pipelineTracker/useSnapshotFreshness';
import { usePrefetchSlowTabs } from '../../hooks/pipelineTracker/usePrefetchSlowTabs';
import { canEditTaxonomy } from '../../hooks/pipelineTracker/reportContract';
import TasksTab from './tabs/TasksTab';
import PipelineTab from './tabs/PipelineTab';
import UploadTab from './tabs/UploadTab';
import LeadSourceTab from './tabs/LeadSourceTab';
import ComplianceTab from './tabs/ComplianceTab';
import TrendsTab from './tabs/TrendsTab';
import OperationsOverviewTab from './tabs/OperationsOverviewTab';
import MarketingTab from './tabs/MarketingTab';
import BrokerRemTab from './tabs/BrokerRemTab';
import BoardPackTab from './tabs/BoardPackTab';

/**
 * SFG / Salestrekker Pipeline Tracker.
 *
 * DELIBERATELY NOT IN THE NAVIGATION. The founder validates this by direct URL
 * first, so the page ships reachable but unlisted. That is precisely what the
 * `observe` rollout state means, and the access hook already encodes it:
 * `canOpen` is true from `observe` upward, `showInNav` only at `enabled`.
 * Nothing here touches either nav surface.
 *
 * ISOLATION: this page shares no data plane with the MyCRM-fed pipeline,
 * funnel or Broker Activity surfaces, which are becoming the separate LMG
 * add-on. The CI gate enforces it in both directions.
 */

type TabKey = 'pipeline' | 'leadSource' | 'operations' | 'rem' | 'marketing' | 'board' | 'upload' | 'trends';

/**
 * Compliance and Tasks are one section, not two tabs.
 *
 * Matt Spears, 8 Sep 2026: "Tasks (should sit under Operations)" and "Compliance (Rename to
 * Operations)". They are the same question asked twice: how well is the back office running
 * this book. Splitting them across the top nav made a reader compare file quality and task
 * load by memory.
 */
type OpsSection = 'overview' | 'compliance' | 'tasks';

interface OutletShape {
  userData: { companyId?: string; role?: string } | null;
  companyData: { featureFlags?: Record<string, unknown> } | null;
}

const TABS: Array<{ key: TabKey; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { key: 'pipeline', label: 'Pipeline', icon: Workflow },
  { key: 'leadSource', label: 'Leads & Partners', icon: Route },
  { key: 'operations', label: 'Operations', icon: ShieldCheck },
  { key: 'rem', label: 'Broker REM', icon: Wallet },
  // V6: spend entered by the practice, over leads from the export. It asks the Leads & Partners
  // question from the cost side: what did these leads cost.
  { key: 'marketing', label: 'Marketing', icon: Megaphone },
  { key: 'board', label: 'Board pack', icon: FileText },
  { key: 'trends', label: 'Trends', icon: LineChart },
  { key: 'upload', label: 'Upload', icon: Upload },
];

// Overview first and the default (V6, founder decision 28 Sep 2026): the live File quality and
// Tasks views are kept as they are, and the new Overview gathers what the back office is asked
// about first.
const OPS_SECTIONS: Array<{ key: OpsSection; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { key: 'overview', label: 'Overview', icon: Gauge },
  { key: 'compliance', label: 'File quality', icon: ShieldCheck },
  { key: 'tasks', label: 'Tasks', icon: ListChecks },
];

const PipelineTrackerPage: React.FC = () => {
  const { userData, companyData } = useOutletContext<OutletShape>();
  const companyId = userData?.companyId;
  const access = usePipelineTrackerAccess(companyId, companyData?.featureFlags);
  const [tab, setTab] = useState<TabKey>('pipeline');
  const [opsSection, setOpsSection] = useState<OpsSection>('overview');

  /**
   * The as-at date of the newest published snapshot, and whether it has aged.
   *
   * Read from the tracker status the ingestion writes, so it reflects what is
   * actually published rather than what happens to be rendered on the open tab.
   * Seven days matches the weekly export cadence the freshness sweep uses.
   */
  const freshness = useSnapshotFreshness(companyId);

  // Operations and the Board pack take longest to answer; ask for them once the Pipeline figures
  // are in, so switching across finds them already loaded.
  usePrefetchSlowTabs(companyId);

  // Fail closed while the server entitlement is still resolving. Rendering the
  // report first and retracting it would flash data at someone who may not be
  // entitled to it.
  if (access.loading) {
    return (
      <div className="space-y-6">
        <PageHeader icon={Workflow} title="SalesTrekker" subtitle="Checking access" />
      </div>
    );
  }

  if (!access.canOpen) {
    return (
      <div className="space-y-6">
        <PageHeader icon={Workflow} title="SalesTrekker" />
        <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-8 text-center">
          <Lock className="mx-auto mb-3 h-8 w-8 text-slate-500" />
          <p className="text-slate-300">SalesTrekker is not enabled for your organisation.</p>
          <p className="mt-1 text-sm text-slate-500">
            It is an add-on for practices reporting from Salestrekker exports.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Workflow}
        title="SalesTrekker"
        subtitle="Deals in flight and task compliance, from your weekly Salestrekker export"
        // The badge states the rollout honestly rather than hiding it. Someone
        // looking at an unlisted page should know why it is unlisted.
        badge={access.rollout === 'observe' ? 'Preview' : undefined}
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            {/* Freshness, stated in the header where a reader looks first. The
                figures below are only as current as the last upload, and a page
                that does not say so invites being read as live. */}
            {freshness && (
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                freshness.stale
                  ? 'border-amber-500/30 bg-amber-500/15 text-amber-300'
                  : 'border-green-500/30 bg-green-500/15 text-green-300'
              }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${freshness.stale ? 'bg-amber-400' : 'bg-green-400'}`} />
                {freshness.label}
              </span>
            )}
            <button
              type="button"
              onClick={() => setTab('upload')}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-500"
            >
              Upload export
            </button>
          </div>
        )}
      />

      <div className="border-b border-slate-700">
        <nav className="-mb-px flex gap-6" aria-label="SalesTrekker sections">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              aria-current={tab === key ? 'page' : undefined}
              className={`flex items-center gap-2 border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                tab === key
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-slate-400 hover:border-slate-600 hover:text-slate-200'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'pipeline' && companyId && <PipelineTab companyId={companyId} role={userData?.role} />}
      {tab === 'leadSource' && companyId && <LeadSourceTab companyId={companyId} />}
      {tab === 'marketing' && companyId && <MarketingTab companyId={companyId} role={userData?.role} />}

      {tab === 'operations' && companyId && (
        <div className="space-y-5">
          <div
            className="inline-flex rounded-lg border border-slate-700 bg-slate-900/60 p-0.5"
            role="group"
            aria-label="Operations section"
          >
            {OPS_SECTIONS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                aria-pressed={opsSection === key}
                onClick={() => setOpsSection(key)}
                className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-bold tracking-wide transition-colors ${
                  opsSection === key
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
          {opsSection === 'overview' && <OperationsOverviewTab companyId={companyId} onOpenTasks={() => setOpsSection('tasks')} canEdit={canEditTaxonomy(userData?.role)} />}
          {opsSection === 'compliance' && <ComplianceTab companyId={companyId} />}
          {opsSection === 'tasks' && <TasksTab companyId={companyId} />}
        </div>
      )}

      {tab === 'rem' && companyId && <BrokerRemTab companyId={companyId} role={userData?.role} />}
      {tab === 'board' && companyId && <BoardPackTab companyId={companyId} />}
      {tab === 'trends' && companyId && <TrendsTab companyId={companyId} />}
      {tab === 'upload' && companyId && <UploadTab companyId={companyId} />}
    </div>
  );
};


export default PipelineTrackerPage;
